import { getAppUrl } from "@/lib/utils";
import { NY_MARBLE_PHONE_NUMBER } from "@marble/core/constants/phone";
import { State } from "@prisma/client";

const MARBLE_VCARD_DISPLAY_NAME_PREFIX = "Marble Health";
const MARBLE_VCARD_EMAIL = "hello@marblehealth.com";

export const getLinkHtml = (url: string, text: string) =>
  `<a href="${url}" style="color: #1093ad; text-decoration: none;" aria-label="${text}">${text}</a>`;

export const getButtonHtml = ({
  url,
  text,
  backgroundColor = "#808080",
}: {
  url: string;
  text: string;
  backgroundColor?: string;
}) => {
  return `
		<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="margin: 20px 0;">
			<tr>
				<td align="center" style="padding: 0;">
					<table role="presentation" cellspacing="0" cellpadding="0" border="0" class="email-button">
						<tr>
							<td align="center" style="background-color: ${backgroundColor}; border-radius: 10px; padding: 12px 24px; mso-padding-alt: 12px 24px;">
								<a href="${url}" target="_blank" rel="noopener noreferrer" role="button" aria-label="${text} - Opens in a new tab" style="display: inline-block; color: #ffffff !important; font-size: 16px; font-weight: 500; text-decoration: none !important; line-height: 1.5; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">${text}</a>
							</td>
						</tr>
					</table>
				</td>
			</tr>
		</table>
	`;
};

export const MARBLE_AUTOMATED_TEXT_MESSAGE_SUFFIX =
  "Automated message from Marble Health";

export const MARBLE_CONTACT_NAME = "Jada";

const PLACEHOLDER_GREETING_NAMES = new Set([
  "unknown",
  "n/a",
  "n.a.",
  "n.a",
  "none",
  "tbd",
  "null",
]);

/**
 * Names we should not interpolate into "Hi {name}" — empty values and
 * placeholders counselors type when they do not know the parent's name.
 */
export const isUsableGreetingName = (
  name: string | null | undefined,
): name is string => {
  const trimmed = name?.trim();
  if (!trimmed) {
    return false;
  }
  return !PLACEHOLDER_GREETING_NAMES.has(trimmed.toLowerCase());
};

/**
 * English possessive for a name interpolated into SMS copy.
 * Names ending in s take an apostrophe only ("James' appointment");
 * every other name takes 's ("Jane's appointment").
 *
 * `User.firstName` is nullable, so this accepts null/undefined and returns
 * "their" rather than throwing on `.trim()` — insurance notices and
 * appointment confirmations/reminders all go through here.
 */
export const possessiveName = (name: string | null | undefined): string => {
  const trimmed = name?.trim() ?? "";
  if (!trimmed) {
    return "their";
  }
  const last = trimmed[trimmed.length - 1];
  if (last === "s" || last === "S") {
    return `${trimmed}'`;
  }
  return `${trimmed}'s`;
};
export const MARBLE_CONTACT_AVATAR_URL = "jada.jpg";

/**
 * Formats an E.164 phone number to (XXX) XXX-XXXX format.
 */
export const formatMarblePhoneNumber = (phoneNumber: string): string => {
  // Extract the last 10 digits (US phone numbers)
  const digits = phoneNumber.replace(/\D/g, "").slice(-10);
  if (digits.length !== 10) {
    return phoneNumber; // Return original if we can't format
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/**
 * Standard fallback when a state-configured phone number is unavailable.
 * This deliberately stays state-agnostic so adding a state never requires
 * adding another phone number to code.
 *
 * Prefer `getMarblePhoneNumber` from `@/lib/modules/states/phone` (async,
 * Redis-cached via getStateConfig) for server-side and Twilio usage.
 */
export const getFallbackMarblePhoneNumber = ({
  formatted,
}: {
  formatted?: boolean;
}): string => {
  return formatted
    ? formatMarblePhoneNumber(NY_MARBLE_PHONE_NUMBER)
    : NY_MARBLE_PHONE_NUMBER;
};

/**
 * Builds a vCard 3.0 string for Marble Health (state-specific cell number).
 * Line endings are CRLF for MCS upload and contact apps.
 */
export const buildMarbleVCardContent = (
  state: State,
  phoneNumber?: string,
): string => {
  const tel =
    phoneNumber ??
    getFallbackMarblePhoneNumber({
      formatted: false,
    });
  const portalUrl = getAppUrl("/portal/login", "https://app.marblehealth.com");
  const lines = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `N:${MARBLE_VCARD_DISPLAY_NAME_PREFIX};;;;`,
    `FN:${MARBLE_VCARD_DISPLAY_NAME_PREFIX}`,
    `ORG:${MARBLE_VCARD_DISPLAY_NAME_PREFIX}`,
    `TEL;TYPE=CELL:${tel}`,
    `EMAIL;TYPE=INTERNET:${MARBLE_VCARD_EMAIL}`,
    `URL:${portalUrl}`,
    "END:VCARD",
  ];
  return lines.join("\r\n");
};

// A reply is affirmative only when it contains one of these words and every
// other word is in ALLOWABLE_CONFIRM_COURTESY_WORDS
const ALLOWABLE_CONFIRM_WORDS = new Set([
  // English
  "yes",
  "yess",
  "yyes",
  "y",
  "k",
  "confirm",
  "confirmed",
  "works",
  "okay",
  "ok",
  "yea",
  "ye",
  "yeah",
  "yep",
  // Spanish
  "si",
  "sí",
  "bien",
  "confirmo",
  "confirmar",
  "confirmada",
  "confirmado",
  // Chinese (Mandarin/Cantonese)
  "是",
  // Haitian Creole
  "wi",
]);

// Greetings and thanks that can sit beside an affirmative ("ok thank you",
// "hola confirmado") without changing what it means. Any other word -- "no",
// a time, a question -- sends the reply to ops and Marty instead, since
// "Si no hasta las 6" contains "si" but means "not until 6".
const ALLOWABLE_CONFIRM_COURTESY_WORDS = new Set([
  "hi",
  "hello",
  "hola",
  "thanks",
  "thank",
  "thankyou",
  "you",
  "please",
  "gracias",
]);

// Matches elongated "yes" variants like "yeeesss", "yeees", "yesss", etc.
const ELONGATED_YES_REGEX = /^y+e+s+$/;

export function getIsAffirmativeResponse(message: string) {
  if (!message) return false;

  // Normalize accents
  const normalized = message
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "") // remove accents
    .toLowerCase()
    .trim();

  // Split by any non-word character (space, punctuation, dash, etc.)
  const tokens = normalized
    .split(/[^a-zA-Z0-9\u4e00-\u9fff]+/)
    .filter((token) => token.length > 0);

  return (
    tokens.some(_isConfirmWord) &&
    tokens.every(
      (token) =>
        _isConfirmWord(token) || ALLOWABLE_CONFIRM_COURTESY_WORDS.has(token),
    )
  );
}

export const PATIENT_PORTAL_INSURANCE_LINK = getAppUrl(
  "/portal/insurance/update",
);
export const PATIENT_PORTAL_APPOINTMENT_SCHEDULE_LINK =
  getAppUrl("/portal/schedule");

function _isConfirmWord(token: string) {
  return ALLOWABLE_CONFIRM_WORDS.has(token) || ELONGATED_YES_REGEX.test(token);
}
